import { Base } from '@studiometa/js-toolkit';
import { morph } from '../../../utils/morph.js';

/**
 * Variant Selects
 */
export default class VariantSelects extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'VariantSelects',
    refs: ['disabledBtn[]'],
    emits: ['update-availability'],
  };

  /**
   * mounted
   */
  mounted() {
    this.updateOptions();
    this.initializeProductForm();
    [this.currentColor] = this.options;
  }

  /**
   * When changing variation
   * @param {Event} event The change event from the select element
   * @returns {void}
   */
  onChange(event) {
    if (!event || !event.target) return;

    const selectedOption = event.target;

    this.options = Array.from(this.$el.querySelectorAll('select'), (select) => select.value);
    this.updateSelectedOption(selectedOption);

    // Check if we're on a product page (not in a card or dialog)
    const isOnProductPage =
      this.$el.dataset.templateProductMatch === 'true' &&
      !event.target.closest('quick-add-dialog');
    
    // Check if this is a combined listing variant change
    const currentUrl = this.$el.dataset.url?.split('?')[0];
    console.log('isOnProductPage', isOnProductPage);
    console.log('currentUrl', currentUrl);
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    
    // Fetch updated section content
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    // Update URL in browser
    // this.updateBrowserUrl(selectedOption, loadsNewProduct);

    this.$update();
  }

  /**
   * Update the selected option (like Horizon's updateSelectedOption)
   * @param {HTMLElement} target The selected input element
   * @returns {void}
   */
  updateSelectedOption(target) {
    if (!target) return;

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) return;

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Build request URL for fetching updated content (like Horizon's buildRequestUrl)
   * @param {HTMLElement} selectedOption The selected option element
   * @returns {string} The request URL
   */
  buildRequestUrl(selectedOption) {
    // Use connected product URL if available, otherwise use current product URL
    const productUrl = selectedOption.dataset.connectedProductUrl || this.$el.dataset.url;

    const params = [];

    // Add selected option values
    if (this.selectedOptionsValues.length) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    }

    // Add section ID for fetching the right section
    const sectionId = this.$el.dataset.section;
    if (sectionId) {
      params.push(`section_id=${sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Get selected options values (like Horizon)
   * @returns {string[]} Array of selected option value IDs
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.$el.querySelectorAll('input:checked, select option[selected]'),
    );

    return selectedOptions
      .map((option) => {
        const optionValueId = option.dataset.optionValueId;
        if (!optionValueId) {
          console.warn('No option value ID found for option:', option);
          return '';
        }
        return optionValueId;
      })
      .filter(Boolean);
  }

  /**
   * Open Restock popup
   * @returns {void}
   */
  onDisabledBtnClick() {
    if (document.querySelector('.restock-alerts-notify-button')) {
      setTimeout(() => {
        document.querySelector('.restock-alerts-notify-button').click();
      }, 200);
    }
  }

  /**
   * Fetch updated section content (like Horizon's fetchUpdatedSection)
   * @param {string} requestUrl The URL to fetch
   * @param {boolean} shouldMorphMain Whether to morph the entire main content
   * @returns {void}
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // Abort any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateVariantPicker(html);
        this.updateVariantQuickAddSticky(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error('Error fetching updated section:', error);
        }
      });
  }

  /**
   * Update just the variant picker and related elements
   * @param {Document} newHtml The new HTML document
   * @returns {void}
   */
  updateVariantPicker(newHtml) {
    // Update variant picker
    const newVariantPicker = newHtml.querySelector(`#${this.$el.id}`);
    if (newVariantPicker) {
      morph(this.$el, newVariantPicker);
    }

    // Update price
    const priceElement = document.querySelector(`#price-${this.$el.dataset.section}`);
    const newPriceElement = newHtml.querySelector(`#price-${this.$el.dataset.section}`);
    if (priceElement && newPriceElement) {
      morph(priceElement, newPriceElement);
    }

    // Update add to cart form
    const formElement = document.querySelector('#main-add-to-cart');
    const newFormElement = newHtml.querySelector('#main-add-to-cart');
    if (formElement && newFormElement) {
      morph(formElement, newFormElement);
    }

    console.log('Updated variant picker and related elements');
    this.$parent.$update();
  }

    /**
   * Update just the variant picker and related elements
   * @param {Document} newHtml The new HTML document
   * @returns {void}
   */
  updateVariantQuickAddSticky(newHtml) {
    // Update price
    const priceElement = document.querySelector(`#sticky-add-to-cart #price-${this.$el.dataset.section}`);
    const newPriceElement = newHtml.querySelector(`#price-${this.$el.dataset.section}`);
    if (priceElement && newPriceElement) {
      morph(priceElement, newPriceElement);
    }

    // Update add to cart form
    const formElement = document.querySelector('#sticky-add-to-cart');
    const newFormElement = newHtml.querySelector('#sticky-add-to-cart');
    if (formElement && newFormElement) {
      morph(formElement, newFormElement);
    }

    console.log('Updated variant picker and related elements');
    this.$parent.$update();
  }

  /**
   * Update browser URL (like Horizon)
   * @param {HTMLElement} selectedOption The selected option
   * @param {boolean} loadsNewProduct Whether this loads a new product
   * @returns {void}
   */
  updateBrowserUrl(selectedOption, loadsNewProduct) {
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId;

    // Update variant parameter
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    // Update path for combined listings
    if (loadsNewProduct) {
      const newUrl = selectedOption.dataset.connectedProductUrl;
      if (newUrl) {
        url.pathname = newUrl;
      }
    }

    // Update browser history
    if (url.href !== window.location.href) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Update Options
   * @returns {void}
   */
  updateOptions() {}

  /**
   * Initialize component with proper product form reference
   * @returns {void}
   */
  initializeProductForm() {
    this.productForm = document.querySelector('.main-add-to-cart form');
    if (!this.productForm) {
      this.productForm = document.querySelector('#main-add-to-cart form');
    }
  }
}
